/**
 * Enter-to-advance for forms.
 *
 * Pressing Enter inside a form field is prevented from submitting the form and
 * instead advances focus to the next focusable element (if any). Excludes
 * radio/checkbox (they use Space), disabled elements, and hidden inputs. Enter
 * keeps its native behavior in <textarea> (newline) and on buttons/submit
 * (activation), and honors a data-no-enter-advance opt-out.
 *
 * Advancing skips controls marked `data-skip-enter` (camera triggers, row ×,
 * Cancel) as well as tabindex="-1". Enter on the LAST field — nothing but
 * buttons left before the form's Save — presses the form's primary save: the
 * enabled control marked `data-enter-save` (FormActions' Save carries it; a
 * page with its own Save button adds the attribute). The page's save handler
 * then runs its own validation (`useFieldErrors`), exactly like a click.
 *
 * Preferred usage — the hook attaches a NATIVE keydown listener on the form.
 * React's delegated `onKeyDown` on a <form> proved unreliable in this app (the
 * synthetic handler did not fire for some fields), so we bind natively instead:
 *
 *   import { useEnterKeyAdvance } from "../../Util/handleEnterKey.js";
 *   const formRef = useEnterKeyAdvance();
 *   <form ref={formRef} onSubmit={onSubmit}>
 *
 * `handleEnterKey` (default export) is the equivalent React onKeyDown handler,
 * kept for any callers still wiring it that way.
 */
import { useCallback, useRef } from "react";

// tabindex="-1" marks controls deliberately kept out of the keyboard path
// (row × buttons, stepper buttons, mouse-only helpers) — Enter skips them too.
const FOCUSABLE = [
    "input:not([disabled]):not([type=radio]):not([type=checkbox]):not([type=hidden]):not([tabindex='-1'])",
    "select:not([disabled]):not([tabindex='-1'])",
    "textarea:not([disabled]):not([tabindex='-1'])",
    "button:not([disabled]):not([tabindex='-1'])",
].join(", ");

// Controls Enter-advance may land on (data-skip-enter ones are passed over).
const advanceTargets = (form) =>
    Array.from(form.querySelectorAll(FOCUSABLE)).filter((el) => el.dataset?.skipEnter == null);

const isField = (el) => el.tagName !== "BUTTON" && el.type !== "submit" && el.type !== "button";

// The form's primary save, if it marks one. Also looks for a save button tied
// to the form by `form="<id>"`.
function primarySave(form) {
    const own = form.querySelector("[data-enter-save]");
    const linked = !own && form.id ? document.querySelector(`[data-enter-save][form="${form.id}"]`) : null;
    const btn = own || linked;
    return btn && !btn.disabled && btn.getAttribute("aria-disabled") !== "true" ? btn : null;
}

// Move focus from `el` to the next focusable field within its owning <form>.
// Exported so components that intercept Enter on their own inner input (e.g.
// native <datalist> inputs) can reuse the exact same advance logic.
export function focusNextField(el) {
    const form = el?.form;
    if (!form) return;
    const focusable = advanceTargets(form);
    const idx = focusable.indexOf(el);
    if (idx !== -1 && idx < focusable.length - 1) focusable[idx + 1].focus();
}

// Core: on Enter within `form`, stop implicit submit and advance focus.
function advanceOnEnter(e, form) {
    if (e.key !== "Enter") return;
    // A field's own handler may have already handled Enter — don't double-act.
    if (e.defaultPrevented) return;

    const active = document.activeElement;
    if (!active) return;

    // Let Enter behave natively: newline in textareas, activation on buttons.
    if (active.tagName === "TEXTAREA") return;
    if (active.tagName === "BUTTON" || active.type === "submit") return;
    if (active.dataset?.noEnterAdvance != null) return; // page opt-out (e.g. quick-entry)

    const focusable = advanceTargets(form);
    const idx = focusable.indexOf(active);
    if (idx === -1) return;

    // Always stop the field's Enter from submitting the form…
    e.preventDefault();
    // …on the last field (only buttons after it), press the primary save…
    const save = primarySave(form);
    if (save && !focusable.slice(idx + 1).some(isField)) {
        save.click();
        return;
    }
    // …otherwise advance to the next field if there is one.
    if (idx < focusable.length - 1) focusable[idx + 1].focus();
}

// Hook: returns a callback ref to attach to a <form>. Binds a native keydown
// listener when the form mounts and cleans it up when it unmounts/changes — a
// callback ref (not useRef+useEffect) so it works even if the form is rendered
// conditionally / after an initial loading render.
export function useEnterKeyAdvance() {
    const cleanupRef = useRef(null);
    return useCallback((form) => {
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        if (!form) return;
        const onKeyDown = (e) => advanceOnEnter(e, form);
        form.addEventListener("keydown", onKeyDown);
        cleanupRef.current = () => form.removeEventListener("keydown", onKeyDown);
    }, []);
}

// React onKeyDown handler equivalent (kept for backward compatibility).
function handleEnterKey(e) {
    advanceOnEnter(e, e.currentTarget);
}

export default handleEnterKey;
